// driver.js is CommonJS (module.exports = Driver). A namespace import gives a
// non-constructible namespace object under Babel's interop, which is why
// `new Driver(...)` threw in every test that touched this module. The default
// import resolves to module.exports under both Babel and webpack.
import Driver from "driver.js";
import "driver.js/dist/driver.min.css";

const DriverMaster = {};

const closeCbk = () => {
  DriverMaster.isDriverActive = false;
};

/*
 * driver.js paints a full-screen overlay before it positions the highlight. If
 * a step's target is not in the DOM it throws mid-mount ("Cannot read
 * properties of undefined (reading 'beforeMount')") from inside its own
 * promise, so the try/catch around start() never sees it -- and the overlay is
 * left behind, invisible and swallowing every click on the page.
 *
 * That is the "page goes unclickable a second after load" bug: tours are fired
 * on a timer (see Utils.triggerAddStagesDriver) and target elements that only
 * exist on some pages, so landing anywhere else armed the overlay.
 */
const DRIVER_DOM = [
  "#driver-page-overlay",
  "#driver-highlighted-element-stage",
  "#driver-popover-item"
];

// Classes driver.js stamps onto the PAGE's own elements. driver-fix-stacking
// rewrites z-index and position on every ancestor of the highlight, so leaving
// them behind breaks layout long after the tour is gone.
const DRIVER_CLASSES = [
  "driver-fix-stacking",
  "driver-highlighted-element",
  "driver-position-relative",
  "driver-stage-no-animation",
  "driver-disabled"
];

// Anything driver.js left behind when it is not actually running.
const clearOrphanOverlay = () => {
  if (DriverMaster.isDriverActive) return;
  DRIVER_DOM.forEach(selector => {
    document.querySelectorAll(selector).forEach(node => node.remove());
  });
  DRIVER_CLASSES.forEach(className => {
    document
      .querySelectorAll(`.${className}`)
      .forEach(node => node.classList.remove(className));
  });
};

// A step whose element is not on this page cannot be highlighted, and trying
// is what breaks the overlay. Elements are resolved at start time because the
// step list is static but the DOM is not.
const presentSteps = (steps = []) =>
  steps.filter(step => {
    if (!step?.element) return false;
    try {
      return !!document.querySelector(step.element);
    } catch (e) {
      // A malformed selector is a broken step, not a reason to crash.
      return false;
    }
  });

DriverMaster.killDriver = () => {
  if (DriverMaster.isDriverActive && DriverMaster.driverInstance) {
    DriverMaster.driverInstance.reset();
  }
  DriverMaster.isDriverActive = false;
  clearOrphanOverlay();
};

const driverOptions = {
  animate: true,
  allowClose: false,
  onReset: closeCbk
};
DriverMaster.driverInstance = new Driver(driverOptions);

DriverMaster.execute = steps => {
  if (!steps?.length) return undefined;

  // Clear anything a previous failed tour left armed before adding to it.
  clearOrphanOverlay();

  const runnable = presentSteps(steps);
  if (!runnable.length) return false;

  let result = "";
  DriverMaster.isDriverActive = true;
  const { driverInstance } = DriverMaster;
  driverInstance.defineSteps(runnable);
  try {
    driverInstance.start();
    result = true;
  } catch (e) {
    console.error("Driver initate failed", e);
    DriverMaster.isDriverActive = false;
    clearOrphanOverlay();
    result = false;
  }
  return result;
};

export default DriverMaster;
export { DriverMaster };
